// services/branchAppointmentConfigService.js
import api from "./api";

// ========== APPOINTMENT CONFIGURATION ==========

/**
 * Get branch appointment configuration
 */
const getAppointmentConfig = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/appointment-config`
  );
  return response.data;
};

/**
 * Update branch appointment configuration
 */
const updateAppointmentConfig = async (branchCode, configData) => {
  const response = await api.put(
    `/admin-branches/${branchCode}/appointment-config`,
    configData
  );
  return response.data;
};

// ========== DAY BUFFERS MANAGEMENT ==========

/**
 * Get day buffers configuration
 */
const getDayBuffers = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/appointment-config`
  );
  return {
    data: {
      dayBuffers:
        response.data.data?.appointmentConfig?.schedulingRules?.dayBuffers,
      branchCode,
    },
  };
};

/**
 * Update day buffers (start/end of day)
 */
const updateDayBuffers = async (branchCode, dayBuffers) => {
  const response = await api.patch(
    `/admin-branches/${branchCode}/appointment-config/day-buffers`,
    dayBuffers
  );
  return response.data;
};

/**
 * Bulk update day buffers across multiple branches
 */
const bulkUpdateDayBuffers = async (branchCodes, dayBuffers) => {
  const response = await api.post(`/admin-branches/day-buffers/bulk`, {
    branchCodes,
    dayBuffers,
  });
  return response.data;
};

// ========== BLOCKED PERIODS MANAGEMENT ==========

/**
 * Get all blocked periods for a branch
 */
const getBlockedPeriods = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/blocked-periods`
  );
  return response.data;
};

/**
 * Add a new blocked period
 */
const addBlockedPeriod = async (branchCode, blockedPeriod) => {
  const response = await api.post(
    `/admin-branches/${branchCode}/blocked-periods`,
    blockedPeriod
  );
  return response.data;
};

/**
 * Update a blocked period
 */
const updateBlockedPeriod = async (branchCode, blockIndex, updates) => {
  const response = await api.put(
    `/admin-branches/${branchCode}/blocked-periods/${blockIndex}`,
    updates
  );
  return response.data;
};

/**
 * Delete a blocked period
 */
const deleteBlockedPeriod = async (branchCode, blockIndex) => {
  const response = await api.delete(
    `/admin-branches/${branchCode}/blocked-periods/${blockIndex}`
  );
  return response.data;
};

/**
 * Toggle blocked period active status
 */
const toggleBlockedPeriod = async (branchCode, blockIndex) => {
  const response = await api.patch(
    `/admin-branches/${branchCode}/blocked-periods/${blockIndex}/toggle`
  );
  return response.data;
};

/**
 * Bulk add blocked periods across multiple branches
 */
const bulkAddBlockedPeriods = async (branchCodes, blockedPeriod) => {
  const response = await api.post(`/admin-branches/blocked-periods/bulk`, {
    branchCodes,
    blockedPeriod,
  });
  return response.data;
};

/**
 * Copy blocked periods from one branch to others
 */
const copyBlockedPeriods = async (sourceBranchCode, targetBranchCodes) => {
  const response = await api.post(`/admin-branches/blocked-periods/copy`, {
    sourceBranchCode,
    targetBranchCodes,
  });
  return response.data;
};

/**
 * Compare blocked periods across branches
 */
const compareBlockedPeriods = async (branchCodes = null) => {
  const params = branchCodes ? { branchCodes: branchCodes.join(",") } : {};
  const response = await api.get(`/admin-branches/blocked-periods/compare`, {
    params,
  });
  return response.data;
};

// ========== WORKING HOURS & SCHEDULE ==========

/**
 * Check if branch is open at specific time
 */
const checkIsOpen = async (branchCode, dateTime = null) => {
  const response = await api.post(`/admin-branches/${branchCode}/is-open`, {
    dateTime,
  });
  return response.data;
};

/**
 * Get working hours for specific date
 */
const getWorkingHours = async (branchCode, date = null) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/working-hours`,
    {
      params: { date },
    }
  );
  return response.data;
};

/**
 * Get today's schedule
 */
const getTodaySchedule = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/schedule/today`
  );
  return response.data;
};

/**
 * Get next opening time
 */
const getNextOpening = async (branchCode) => {
  const response = await api.get(`/admin-branches/${branchCode}/next-opening`);
  return response.data;
};

// ========== HOLIDAYS ==========

/**
 * Check if date is a holiday
 */
const checkIsHoliday = async (branchCode, date) => {
  const response = await api.post(`/admin-branches/${branchCode}/is-holiday`, {
    date,
  });
  return response.data;
};

/**
 * Get holidays in date range
 */
const getHolidays = async (branchCode, startDate = null, endDate = null) => {
  const response = await api.get(`/admin-branches/${branchCode}/holidays`, {
    params: { startDate, endDate },
  });
  return response.data;
};

// ========== CAPACITY MANAGEMENT ==========

/**
 * Check branch capacity for date
 */
const checkCapacity = async (branchCode, date = null) => {
  const response = await api.post(
    `/admin-branches/${branchCode}/check-capacity`,
    {
      date,
    }
  );
  return response.data;
};

/**
 * Update appointment statistics
 */
const updateAppointmentStats = async (branchCode, statsData) => {
  const response = await api.post(
    `/admin-branches/${branchCode}/stats/appointments`,
    statsData
  );
  return response.data;
};

// ========== EXCHANGE CHARGES ==========

/**
 * Get GOLD Exchange Charges Configuration
 */
const getExchangeCharges = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/exchange-charges`
  );
  return response.data;
};

// ========== NEARBY BRANCHES ==========

/**
 * Get nearby branches based on location
 */
const getNearbyBranches = async (latitude, longitude, radiusKm = 10) => {
  const response = await api.post(`/admin-branches/nearby`, {
    latitude,
    longitude,
    radiusKm,
  });
  return response.data;
};

// ========== APPOINTMENT TYPES ==========

/**
 * Get available appointment types for branch
 */
const getAppointmentTypes = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/appointment-types`
  );
  return response.data;
};

/**
 * Update appointment type configuration
 */
const updateAppointmentType = async (branchCode, typeConfig) => {
  const response = await api.put(
    `/admin-branches/${branchCode}/appointment-types`,
    typeConfig
  );
  return response.data;
};

// ========== AUTO-APPROVAL SETTINGS ==========

/**
 * Get auto-approval settings
 */
const getAutoApprovalSettings = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/auto-approval-settings`
  );
  return response.data;
};

/**
 * Update auto-approval settings
 */
const updateAutoApprovalSettings = async (branchCode, settings) => {
  const response = await api.patch(
    `/admin-branches/${branchCode}/appointment-config/auto-approval`,
    settings
  );
  return response.data;
};

// ========== BOOKING WINDOW ==========

/**
 * Get booking window configuration
 */
const getBookingWindow = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/booking-window`
  );
  return response.data;
};

/**
 * Update booking window configuration
 */
const updateBookingWindow = async (branchCode, windowConfig) => {
  const response = await api.put(
    `/admin-branches/${branchCode}/booking-window`,
    windowConfig
  );
  return response.data;
};

// ========== NOTIFICATION SETTINGS ==========

/**
 * Get notification settings
 */
const getNotificationSettings = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/notification-settings`
  );
  return response.data;
};

/**
 * Update notification settings
 */
const updateNotificationSettings = async (branchCode, settings) => {
  const response = await api.patch(
    `/admin-branches/${branchCode}/appointment-config/notifications`,
    settings
  );
  return response.data;
};

// ========== CANCELLATION POLICY ==========

/**
 * Get cancellation policy
 */
const getCancellationPolicy = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/cancellation-policy`
  );
  return response.data;
};

/**
 * Update cancellation policy
 */
const updateCancellationPolicy = async (branchCode, policy) => {
  const response = await api.put(
    `/admin-branches/${branchCode}/cancellation-policy`,
    policy
  );
  return response.data;
};

// ========== SCHEDULING RULES ==========

/**
 * Get scheduling rules
 */
const getSchedulingRules = async (branchCode) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/appointment-config`
  );
  return {
    data: {
      schedulingRules: response.data.data?.appointmentConfig?.schedulingRules,
      branchCode,
    },
  };
};

/**
 * Update scheduling rules
 */
const updateSchedulingRules = async (branchCode, schedulingRules) => {
  const response = await api.patch(
    `/admin-branches/${branchCode}/appointment-config/scheduling-rules`,
    schedulingRules
  );
  return response.data;
};

// ========== AVAILABLE SLOTS ==========

/**
 * Get available time slots for a date
 */
const getAvailableSlots = async (branchCode, date, appointmentType = null) => {
  const response = await api.get(
    `/admin-branches/${branchCode}/available-slots`,
    {
      params: { date, appointmentType },
    }
  );
  return response.data;
};

/**
 * Get weekly slot overview
 */
const getWeeklySlots = async (branchCode, startDate = null) => {
  const params = startDate ? { startDate } : {};
  const response = await api.get(`/admin-branches/${branchCode}/weekly-slots`, {
    params,
  });
  return response.data;
};

/**
 * Check slot availability
 */
const checkSlotAvailability = async (branchCode, date, timeSlot) => {
  const response = await api.post(
    `/admin-branches/${branchCode}/check-slot-availability`,
    { date, timeSlot }
  );
  return response.data;
};

const branchAppointmentConfigService = {
  // Configuration
  getAppointmentConfig,
  updateAppointmentConfig,

  // Day Buffers
  getDayBuffers,
  updateDayBuffers,
  bulkUpdateDayBuffers,

  // Blocked Periods
  getBlockedPeriods,
  addBlockedPeriod,
  updateBlockedPeriod,
  deleteBlockedPeriod,
  toggleBlockedPeriod,
  bulkAddBlockedPeriods,
  copyBlockedPeriods,
  compareBlockedPeriods,

  // Working Hours & Schedule
  checkIsOpen,
  getWorkingHours,
  getTodaySchedule,
  getNextOpening,

  // Holidays
  checkIsHoliday,
  getHolidays,

  // Capacity
  checkCapacity,
  updateAppointmentStats,

  // Exchange Charges
  getExchangeCharges,

  // Nearby Branches
  getNearbyBranches,

  // Appointment Types
  getAppointmentTypes,
  updateAppointmentType,

  // Auto-Approval
  getAutoApprovalSettings,
  updateAutoApprovalSettings,

  // Booking Window
  getBookingWindow,
  updateBookingWindow,

  // Notifications
  getNotificationSettings,
  updateNotificationSettings,

  // Cancellation Policy
  getCancellationPolicy,
  updateCancellationPolicy,

  // Scheduling Rules
  getSchedulingRules,
  updateSchedulingRules,

  // Slots
  getAvailableSlots,
  getWeeklySlots,
  checkSlotAvailability,
};

export default branchAppointmentConfigService;
